import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const DEFAULT_ERROR_MESSAGE = "An internal error occurred. Please try again later."
const TYPING_DELAY = 500

export default class extends Controller {
  static get targets() { return ["textarea", "toggle", "publish"]; }
  static get values() { return { url: String, publishUrl: String, editable: Boolean, settings: Object }; }

  // `settingsValue` re-parses the data attribute on every access, so keep one local copy: this
  // controller mutates it as the user types and never writes it back.
  connect() {
    this.settings = this.settingsValue;

    this.textareaTargets.forEach(textarea => {
      const limit = parseInt(textarea.getAttribute("maxlength"), 10);

      $(textarea).lilCharacterCount({
        limit: limit,
        warning: limit / 10,
        allowExceed: false,
        counterClass: "length-counter",
        defaultClass: "",
        warningClass: "",
        dangerClass: "",
        submitButton: ""
      });

      // The plugin seeds the counter with the raw limit; recompute it against the text the
      // server already rendered.
      this.counterFor(textarea).innerHTML = limit - textarea.value.length;

      if (this.editableValue) {
        this.toggleControlsFor(textarea, textarea.value.length === 0);
      }
    });

    this.updatePublishButtonState();
  }

  // The plugin binds directly to each textarea with no namespace and no destroy method; a
  // reconnect (Turbo cache restore) would otherwise stack a second counter node and a second
  // input listener alongside the first.
  disconnect() {
    clearTimeout(this.timer);

    this.textareaTargets.forEach(textarea => {
      $(textarea).off("input keyup");
      const counter = this.counterFor(textarea);
      if (counter) { counter.remove(); }
    });
  }

  counterFor(textarea) { return textarea.parentNode.querySelector(".length-counter"); }

  // One debounce shared by every textarea on the page: typing in one field resets whichever save
  // was pending. `textarea` is captured synchronously, since `currentTarget` is already null by
  // the time the timeout runs.
  onTextareaKeyup(event) {
    event.stopPropagation();
    const textarea = event.currentTarget;

    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.saveText(textarea), TYPING_DELAY);
  }

  saveText(textarea) {
    this.toggleControlsFor(textarea, textarea.value.length === 0);
    this.updateSettings(textarea.closest(".text-component").id, "text", textarea.value);
  }

  onToggleChange(event) {
    const input = event.currentTarget;
    const component = input.closest(".text-component, .radio-component");
    this.updateSettings(component.id, "visible", input.checked ? "true" : "false");
  }

  publish() {
    const options = RequestHelper.defaultPutOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({});

    fetch(this.publishUrlValue, options).
      then(response => response.json().then(data => ({ ok: response.ok, data }))).
      then(({ ok, data }) => {
        if (ok) {
          this.settings = data.compliance;
          this.updatePublishButtonState();
          window.ltAlert({ message: data.message, type: "toast-success", duration: 2500 });
        } else {
          window.ltAlert({ message: data.message || DEFAULT_ERROR_MESSAGE, type: "toast-error" });
        }
      }).
      catch(this.logFailedCall);
  }

  // Every disclosure edit and every toggle flip funnels through here, one PUT per change.
  updateSettings(node, type, value) {
    if (this.settings.draft[node] === undefined) {
      this.settings.draft[node] = { [type]: value };
    } else {
      this.settings.draft[node] = Object.assign({}, this.settings.draft[node], { [type]: value });
    }

    this.updatePublishButtonState();

    const options = RequestHelper.defaultPutOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({ compliance: this.settings });

    fetch(this.urlValue, options).
      then(response => response.json().then(data => ({ ok: response.ok, data }))).
      then(({ ok, data }) => {
        if (ok) {
          window.ltAlert({ message: data.message, type: "toast-success", duration: 2500 });
        } else {
          window.ltAlert({ message: data.message || DEFAULT_ERROR_MESSAGE, type: "toast-error" });
        }
      }).
      catch(this.logFailedCall);
  }

  // Rules:
  //  * empty text disables the toggle and forces it off
  //  * text going from empty to non-empty re-enables the toggle and forces it on
  //  * otherwise the toggle is left exactly as the person set it
  // Forcing the checked state dispatches a real "change", which is what persists the resulting
  // visibility through onToggleChange.
  toggleControlsFor(textarea, disable) {
    const component = textarea.closest(".text-component");

    component.querySelectorAll(".toggle-column input").forEach(input => {
      const wasDisabled = input.disabled;
      input.disabled = disable;

      if (disable) {
        input.checked = false;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      } else if (wasDisabled) {
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  updatePublishButtonState() {
    if (!this.hasPublishTarget) { return; }

    this.publishTarget.disabled = JSON.stringify(this.settings.draft) === JSON.stringify(this.settings.published);
  }

  logFailedCall(response) { console.warn(response); }
}
